import { Link } from "../../router";
import type { FlowProcess, PipelineState } from "./advisoryApi";
import { dateTime } from "./format";

// Where the process status sits on the way to orders. pdf_generated is still
// "submitted" from the advisor's point of view; signing (not on this tenant's
// flow) sits where compliance does.
const STAGE: Record<string, number> = {
  submitted: 0,
  pdf_generated: 0,
  compliance_review: 1,
  signing: 1,
  external_review: 2,
  completed: 3,
};

type RowState = "done" | "current" | "upcoming" | "blocked";

const ROW_CLASS: Record<RowState, string> = {
  done: "check-row check-pass",
  current: "check-row check-warn",
  upcoming: "check-row adv-check-open",
  blocked: "check-row check-block",
};
const ROW_GLYPH: Record<RowState, string> = { done: "✓", current: "…", upcoming: "", blocked: "!" };

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The advice's way to orders, as a check-row chain:
 * Submitted → Compliance review (in Quantfolio) → External review (Andersen's
 * pre-order gate) → Completed → Orders in the order inbox.
 *
 * The server drives the gate and the inbox on every status read (poll or
 * Refresh), so this works without Quantfolio's webhooks reaching Andersen.
 */
export function OrderPipeline({
  process,
  sessionStatus,
  pipeline,
  onHandoff,
  handing,
  onSync,
  syncing,
  syncError,
}: {
  process: FlowProcess | null;
  sessionStatus: string;
  pipeline: PipelineState | null | undefined;
  onHandoff: () => void;
  handing: boolean;
  onSync: () => void;
  syncing: boolean;
  syncError: string | null;
}) {
  const status = process?.status ?? sessionStatus;
  if (status === "cancelled" || status === "canceled") return null;
  const idx = STAGE[status] ?? -1;
  const rowAt = (i: number): RowState => (idx > i ? "done" : idx === i ? "current" : "upcoming");

  const gate = pipeline?.gate ?? null;
  const inbox = pipeline?.inbox;
  const found = (inbox?.orders ?? 0) + (inbox?.savingsPlans ?? 0);
  const approvals = (process?.approvals ?? []).filter((a) => a.stepType === "compliance");
  const compliance = approvals[approvals.length - 1];
  const signing = status === "signing";

  const gateState: RowState = gate?.status === "canceled" ? "blocked" : gate?.status === "executed" ? "done" : rowAt(2);
  const inboxState: RowState = found > 0 ? "done" : idx === 3 ? "current" : "upcoming";

  return (
    <section className="adv-subsection adv-pipeline">
      <div className="adv-pipeline-head">
        <span className="adv-label">From advice to orders</span>
        <span className="adv-pipeline-actions">
          {inbox?.lastIngestAt && <span className="muted">Inbox checked {dateTime(inbox.lastIngestAt)}</span>}
          <button type="button" className="btn btn-ghost adv-btn" onClick={onSync} disabled={syncing}>
            {syncing ? "Refreshing…" : "Refresh"}
          </button>
        </span>
      </div>
      {syncError && <div className="banner-error">{syncError}</div>}
      <ul className="check-list adv-pipeline-list">
        <li className={ROW_CLASS[rowAt(0)]}>
          <span className="check-icon">{ROW_GLYPH[rowAt(0)]}</span>
          <span className="adv-pipeline-text">
            <strong>Submitted</strong>
            <span className="muted">
              {idx === 0 ? "Generate the advice document below — that sends it on to compliance." : "The advice is locked and documented."}
            </span>
          </span>
        </li>

        <li className={ROW_CLASS[rowAt(1)]}>
          <span className="check-icon">{ROW_GLYPH[rowAt(1)]}</span>
          <span className="adv-pipeline-text">
            <strong>{signing ? "Client signing" : "Compliance review"} · in Quantfolio</strong>
            <span className="muted">
              {compliance?.decidedAt
                ? `Compliance ${compliance.status}${compliance.decidedByName ? ` by ${compliance.decidedByName}` : ""} · ${dateTime(compliance.decidedAt)}`
                : idx === 1
                  ? "A compliance officer approves it in Quantfolio; this page picks it up by itself."
                  : idx > 1
                    ? "Approved in Quantfolio."
                    : "Next, a compliance officer reviews it in Quantfolio."}
            </span>
          </span>
          {idx <= 1 && (
            <button type="button" className="link-action adv-pipeline-link" onClick={onHandoff} disabled={handing}>
              {handing ? "Opening…" : "Continue in Quantfolio ↗"}
            </button>
          )}
        </li>

        <li className={ROW_CLASS[gateState]}>
          <span className="check-icon">{ROW_GLYPH[gateState]}</span>
          <span className="adv-pipeline-text">
            <strong>External review · Andersen's pre-order gate</strong>
            <span className="muted">
              {gate
                ? gate.status === "pending"
                  ? `Waiting for a decision in Pre-order checks (check #${gate.checkId}${gate.openedBy === "webhook" ? ", opened by Quantfolio" : ""}).`
                  : `${gate.status === "executed" ? "Approved" : "Rejected"}${gate.decidedBy ? ` by ${gate.decidedBy}` : ""}${gate.decidedAt ? ` · ${dateTime(gate.decidedAt)}` : ""}`
                : idx > 2
                  ? "Decided."
                  : idx === 2
                    ? "Opening the check…"
                    : "After compliance, the advice waits here for Andersen's approval."}
            </span>
          </span>
          {(gate || idx === 2) && (
            <Link to="/app/flow-checks" className="link-action adv-pipeline-link">
              Pre-order checks →
            </Link>
          )}
        </li>

        <li className={ROW_CLASS[rowAt(3) === "current" ? "done" : rowAt(3)]}>
          <span className="check-icon">{idx >= 3 ? "✓" : ""}</span>
          <span className="adv-pipeline-text">
            <strong>Completed</strong>
            <span className="muted">{idx >= 3 ? "Quantfolio has generated the portfolio orders." : "Quantfolio generates the orders."}</span>
          </span>
        </li>

        <li className={ROW_CLASS[inboxState]}>
          <span className="check-icon">{ROW_GLYPH[inboxState]}</span>
          <span className="adv-pipeline-text">
            <strong>Orders in the order inbox</strong>
            <span className="muted">
              {found > 0
                ? `${plural(inbox!.orders, "order")}${inbox!.savingsPlans ? ` and ${plural(inbox!.savingsPlans, "savings plan")}` : ""} · ${
                    inbox!.open > 0 ? `${inbox!.open} waiting for the order desk` : "all processed"
                  }`
                : idx === 3
                  ? "Looking for the orders — Quantfolio can take a moment after completing."
                  : "They land here for the order desk to execute."}
            </span>
          </span>
          {found > 0 && (
            <Link to="/app/inbox" className="link-action adv-pipeline-link">
              Order inbox ({found}) →
            </Link>
          )}
        </li>
      </ul>
    </section>
  );
}
